import axios from "axios";

import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { FiAlertTriangle } from "react-icons/fi";
import { BeatLoader } from "react-spinners";

import { loadUser, login, setRole } from "../data/store/reducers/userSlice";
import { useAppDispatch } from "../data/store/hooks";
import { useURL } from "../data/Config";

interface FormType {
  email: string;
  password: string;
}

const SignIn = () => {
  const location = useLocation();
  const sessionExpired = new URLSearchParams(location.search).get(
    "sessionExpired"
  );
  const AlertIcon = FiAlertTriangle as React.ElementType;
  const [loadingSignin, setLoadingSignin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (sessionExpired) {
      // toast.error("Session expired!");
    }
  }, [sessionExpired, navigate]);

  const onSubmit = async (data: FormType) => {
    setLoadingSignin(true);
    setError(null);
    try {
      const res = await axios.post(`${useURL}/auth/login`, data);

      if (
        res.data.user?.role === "professional" ||
        res.data.user?.role === "employer"
      ) {
        setError("Unauthorized to access admin!");
        return;
      }

      dispatch(login(res.data));
      dispatch(setRole(res.data.user?.role || ""));
      await dispatch(loadUser());

      const redirectURL = localStorage.getItem("redirectAfterLogin");
      if (redirectURL) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectURL);
        return;
      }

      navigate("/");
    } catch (error: any) {
      const message = error.response?.data?.error || "An error occurred";
      setError(message);
    } finally {
      setLoadingSignin(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center p-2 justify-center lg:justify-end lg:pr-40"
      style={{
        backgroundImage: `url("/admin.jpg")`,
        backgroundSize: "cover",
      }}
    >
      <div className="absolute inset-0 bg-black/60" />

      <div className="relative bg-white w-full max-w-lg p-8 rounded-md shadow-md z-10">
        <h2 className="text-2xl mb-4">Sign In</h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-6">
            {/* Email Field */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="email" className="text-gray-600 text-sm">
                Admin Email
              </label>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Email is required!",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email!",
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <input
                    id="email"
                    type="email"
                    value={value}
                    onChange={onChange}
                    className="bg-indigo-100/20 shadow-sm border border-indigo-200 rounded-md p-2"
                  />
                )}
              />
              {errors.email && (
                <p className="text-rose-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="password" className="text-gray-600 text-sm">
                Password
              </label>
              <Controller
                name="password"
                control={control}
                rules={{ required: "Password is required!" }}
                render={({ field: { value, onChange } }) => (
                  <input
                    id="password"
                    type="password"
                    value={value}
                    onChange={onChange}
                    className="bg-indigo-100/20 shadow-sm border rounded-md p-2"
                  />
                )}
              />
              {errors.password && (
                <p className="text-rose-500 text-sm mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <p className="text-rose-500 text-sm flex items-center space-x-2">
                <AlertIcon className="text-xl" />
                <span>{error}</span>
              </p>
            )}

            {/* Registration / Forgot Password Links */}
            {/* <div className="flex flex-col space-y-2">
              <p
                className="cursor-pointer text-sm underline hover:text-indigo-600 w-max"
                onClick={() => navigate("/forgot-password")}
              >
                Forgot password?
              </p>
            </div> */}

            {/* Submit Button */}
            {loadingSignin ? (
              <BeatLoader color="#3B82F6" />
            ) : (
              <button
                disabled={loadingSignin}
                type="submit"
                className="border border-indigo-600 bg-indigo-600 py-2 px-8 w-max text-white text-base rounded-full hover:bg-transparent hover:text-black hover:border-black transition"
              >
                Continue as Admin
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;
